import { useEffect, useRef, type ReactNode } from "react";
import { MessageBubble } from "./MessageBubble";
import { TypingDots } from "./TypingDots";
import { Composer } from "./Composer";
import { t } from "../../i18n/strings";
import type { InputMode, InterviewPhase, Turn } from "../../engine/types";
import type { Locale } from "../../templates/types";

export function ChatPane({
  turns,
  phase,
  onSend,
  banner,
  locale = "en",
}: {
  turns: Turn[];
  phase: InterviewPhase;
  // Widened from (text: string) => void to add voice support (§16): the
  // extra parameter is optional and Composer's typed path still calls
  // onSend with a single argument, so every existing caller (sendMessage's
  // `inputMode: InputMode = "typed"` default) is unaffected.
  onSend: (text: string, inputMode?: InputMode) => void;
  /** Rendered above the message list — e.g. SafetyBanner when paused. */
  banner?: ReactNode;
  locale?: Locale;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns.length, phase]);

  const disabled = phase === "paused" || phase === "complete" || phase === "awaiting_model" || phase === "idle";

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-paper">
      {banner}
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
        {turns.map((turn, i) => (
          <MessageBubble key={i} turn={turn} locale={locale} />
        ))}
        {phase === "awaiting_model" && <TypingDots locale={locale} />}
      </div>
      <Composer
        onSend={onSend}
        disabled={disabled}
        locale={locale}
        placeholder={phase === "paused" ? t(locale, "chat.placeholder.paused") : t(locale, "chat.placeholder.default")}
      />
    </div>
  );
}
